import React, {useEffect, useState} from 'react'
import {Alert, Box, Button, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {checkBalanceWraper} from '../../../../pages/checkout/partials/giftCartPayment/giftCartPayment.style'
import {Controller, useForm} from 'react-hook-form'
import GiftCardBalanceField from './giftCardBalanceField'
import useGiftCardBalanceFields from './useGiftCardBalanceFields'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import useGiftCardBalance from '../../../../hooks/use-gift-card-balance'
import PropTypes from 'prop-types'
import {balanceButtonStyle, errorMessageStyle} from './index.style'
import useSitePreference from '../../../../hooks/use-site-preference'
import Recaptcha from '../../../../components/recaptcha'

const GiftCardBalance = ({isOrderTotalZero = false}) => {
    const form = useForm()
    const intl = useIntl()

    const [giftCardError, setGiftCardError] = useState('')
    const [enableBalanceCall, setEnableBalanceCall] = useState(false)

    const [cardNumberForBalance, setCardNumberForBalance] = useState('')
    const [pinForBalance, setPinForBalance] = useState('')

    const fields = useGiftCardBalanceFields({form, prefix: ''})

    const {
        data: giftCardBalance,
        error,
        isError
    } = useGiftCardBalance(cardNumberForBalance, pinForBalance, enableBalanceCall)

    const {data: recaptchaPreference} = useSitePreference('reCAPTCHAEnable')

    const handleRecaptchaToken = (token, onChange) => {
        onChange(token)
    }

    const handleCheckBalance = async () => {
        setEnableBalanceCall(false)
        const isValid = await form.trigger()
        if (isValid) {
            setEnableBalanceCall(true)

            setCardNumberForBalance(form.getValues('giftCardNumber'))
            setPinForBalance(form.getValues('pin'))
        } else {
            setEnableBalanceCall(false)
            setGiftCardError('')
        }
    }

    useEffect(() => {
        if (giftCardBalance?.giftCertificate) {
            // this is used to display fetched balance of gift card
            setGiftCardError(
                intl.formatMessage(
                    {
                        defaultMessage: 'Your current gift card balance is $ {amount}',
                        id: 'cart_checkout.gift_card.check_balance_value.label'
                    },
                    {
                        amount: giftCardBalance?.giftCertificate?.balance
                    }
                )
            )

            // disable the flag so avoid unnecessary call of check balance
            setEnableBalanceCall(false)
        }

        if (isError) {
            // disable the flag so avoid unnecessary call of check balance
            setEnableBalanceCall(false)

            let errorBody
            const getError = async () => {
                if (error?.response instanceof Response) {
                    try {
                        errorBody = await error?.response?.clone()?.json()
                    } catch {
                        const text = await error?.response?.text()
                        try {
                            errorBody = JSON?.parse(text)
                        } catch {
                            errorBody = {message: text}
                        }
                    }
                }

                if (errorBody?.errorCode === 'SYS-1004') {
                    setGiftCardError(errorBody?.error)
                } else {
                    setGiftCardError('Failed to fetch gift card balance, try again!')
                }
            }
            getError()
        }
    }, [giftCardBalance?.giftCertificate, error, isError])

    return (
        <Box>
            <form data-testid="sf-auth-modal-form-register" noValidate>
                {/* error messages */}
                {form.formState.errors?.global && (
                    <Alert status="error" borderColor={'cav_red'}>
                        <AlertIcon color="cav_red" boxSize={4} />
                        <Text mt={0}>{form.formState.errors.global.message}</Text>
                    </Alert>
                )}

                <Box>
                    {/* card number */}
                    <GiftCardBalanceField {...fields?.giftCardNumber} />

                    {/* card pin */}
                    <GiftCardBalanceField {...fields?.pin} />
                </Box>
                {/* recaptcha */}
                {recaptchaPreference?.reCAPTCHAEnable && (
                    <Controller
                        name="recaptcha"
                        rules={fields?.recaptcha?.rules}
                        control={fields?.recaptcha?.control}
                        render={({field: {onChange, value, ref}}) => (
                            <Recaptcha
                                onVerify={(token) => handleRecaptchaToken(token, onChange)}
                                onExpired={() => {
                                    onChange('')
                                }}
                            />
                        )}
                    />
                )}
                {/* recaptcha error */}
                {recaptchaPreference?.reCAPTCHAEnable && fields?.recaptcha?.error && (
                    <Box sx={errorMessageStyle}>{fields?.recaptcha?.error?.message}</Box>
                )}

                <Box>
                    <Button
                        type="button"
                        variant={'link'}
                        onClick={handleCheckBalance}
                        sx={balanceButtonStyle}
                    >
                        <FormattedMessage
                            defaultMessage="Check Balance"
                            id="checkout_payment.gift_card.button.check_balance"
                        />
                    </Button>
                    {/* gift card error message */}
                    {<Text sx={errorMessageStyle}>{giftCardError}</Text>}
                </Box>
            </form>
        </Box>
    )
}

GiftCardBalance.propTypes = {
    isOrderTotalZero: PropTypes.bool
}

export default GiftCardBalance
